import { Button, noop } from '@custom/dx3';
import { Link, Tooltip } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import classNames from 'classnames';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { COPY } from '../../../assets/copy/copy.js';
import { ExternalLinkIcon, LinkIcon } from '../../../assets/svgs';
import { LikeButton } from '../../../components/LikeButton.js';
import { LockedStatus } from '../../../components/LockedStatuses/LockedStatuses';
import {
  LimitedInformation,
  getLimitedClasses,
} from '../../../components/MissionCard/missionCardUtils';
import { RewardsDisplay } from '../../../components/RewardsDisplay/RewardsDisplay';
import { StatusIcons } from '../../../components/StatusIcons/StatusIcons';
import type {
  MissionLockedReason,
  Reward,
} from '../../../store/queries/fragments/mission/missionSubFragments.js';
import { TaskItem, TaskList } from './TaskList.js';

type MissionDetailsProps = {
  id: string;
  name?: null | string;
  rewards?: null | Reward[];
  iLike: boolean;
  description?: null | string;
  expiringDate?: null | Date;
  expiringSoon?: null | boolean;
  repeats?: null | boolean;
  inProgress?: null | boolean;
  tasks?: null | Array<
    | {
        __typename: 'ClickableLink';
        actionName?: null | string;
        displayText?: null | string;
        url?: null | string;
        completed?: null | boolean;
      }
    | {
        __typename: 'Video';
        actionName?: null | string;
        displayText?: null | string;
        url?: null | string;
        completed?: null | boolean;
      }
    | {
        __typename: 'Quiz';
        displayText?: null | string;
        token?: null | string;
        server?: null | string;
        userId?: null | string;
        quizId?: null | string;
        completed?: null | boolean;
      }
    | {
        __typename: 'ExternalTask' | 'ClickThrough';
        displayText?: null | string;
        completed?: null | boolean;
      }
  >;
  locked: MissionLockedReason | null | undefined;
  onChangeMissionLike: (value: boolean) => Promise<void>;
  displayVideo: (i: number) => void;
  displayQuiz: (i: number) => void;
  actionPerf: (params: any) => void;
  refetch: () => void;
  totalLikes: number;
};

export function MissionDetails(props: MissionDetailsProps): JSX.Element {
  const { onChangeMissionLike } = props;
  const timeRef = useRef<NodeJS.Timeout>();

  const performAction = useCallback(
    (task: {
      url?: null | string;
      actionName?: null | string;
      __typename: string;
    }) => {
      if (task.url && task.actionName) {
        props.actionPerf({
          variables: { url: task.url, action: task.actionName },
        });
      }
      if (task.__typename === 'ClickableLink') {
        timeRef.current = setTimeout(() => {
          props.refetch();
        }, 2000);
      }
    },
    [props]
  );

  useEffect(() => {
    return () => {
      if (timeRef.current) clearTimeout(timeRef.current);
    };
  }, []);

  const hasRewards = useMemo(() => {
    return (props.rewards || []).length > 0;
  }, [props.rewards]);

  const displayTasks = useMemo(() => {
    if (!props.tasks) {
      return <></>;
    }

    return props.tasks.map((task, i) => {
      let linkContent: ReactNode;
      let endIcon: ReactNode = undefined;
      if (props.locked) {
        linkContent = <div>{task.displayText}</div>;
      } else {
        switch (task.__typename) {
          case 'ClickableLink':
            linkContent = (
              <Link
                tabIndex={0}
                onClick={() => {
                  performAction(task);
                }}
                onKeyUp={(e) => {
                  if (e.key === 'enter') {
                    performAction(task);
                  }
                }}
                href={task.url ?? ''}
                target="_blank"
                underline="hover"
              >
                {task.displayText}
              </Link>
            );
            endIcon = <ExternalLinkIcon />;
            break;
          case 'Video':
            linkContent = (
              <Link
                onClick={(e) => {
                  e.preventDefault();
                  props.displayVideo(i);
                }}
                onKeyUp={(e) => {
                  if (e.key === 'enter') {
                    e.preventDefault();
                    props.displayVideo(i);
                  }
                }}
                href="#"
                underline="hover"
              >
                {task.displayText}
              </Link>
            );
            break;
          case 'Quiz':
            linkContent = (
              <Link
                onClick={(e) => {
                  e.preventDefault();
                  props.displayQuiz(i);
                }}
                onKeyUp={(e) => {
                  if (e.key === 'enter') {
                    e.preventDefault();
                    props.displayQuiz(i);
                  }
                }}
                href="#"
                underline="hover"
              >
                {task.displayText}
              </Link>
            );
            break;
          case 'ExternalTask':
            linkContent = (
              <div className="external-task">{task.displayText}</div>
            );
            break;
        }
      }

      return (
        <TaskItem
          completed={!!task.completed}
          endIcon={endIcon}
          locked={!!props.locked}
          key={task.displayText}
        >
          {linkContent}
        </TaskItem>
      );
    });
  }, [performAction, props]);

  const percentDone = useMemo(() => {
    if (props.tasks) {
      const completedTasks = props.tasks.filter(
        (task) => task.completed
      ).length;
      const totalTasks = props.tasks.length;
      if (!totalTasks) {
        return 0;
      } else {
        return Math.floor((completedTasks / totalTasks) * 100);
      }
    }
    return 100;
  }, [props.tasks]);

  const [showCopiedMessage, setShowCopiedMessage] = useState(false);

  const handleClickCopyLink = useCallback(() => {
    navigator.clipboard
      ?.writeText(document.location.href)
      .then(() => setShowCopiedMessage(true))
      .then(() => new Promise<void>((res) => setTimeout(res, 2000)))
      .then(() => setShowCopiedMessage(false));
  }, []);

  return (
    <div className="modal-content">
      <div className="mission-modal-mission-name">{props.name}</div>

      {props.description && (
        <div className="mission-modal-mission-description">
          {props.description}
        </div>
      )}

      <div style={{ alignItems: 'center', display: 'flex', gap: '1em' }}>
        <LikeButton
          iLike={props.iLike}
          onChange={onChangeMissionLike}
          totalLikes={props.totalLikes}
        />

        <Tooltip
          onClose={noop}
          onOpen={noop}
          open={showCopiedMessage}
          title={COPY.MODAL.MSG_LINK_COPIED_SUCCESS}
        >
          <Button
            onClick={handleClickCopyLink}
            size="small"
            startIcon={<LinkIcon />}
          >
            {COPY.MODAL.BTN_COPY_LINK}
          </Button>
        </Tooltip>
        {/* Expand so status icons are right-aligned */}
        <div style={{ flexGrow: 1 }} />

        <div className="small-text">
          <StatusIcons
            expiringDate={props.expiringDate}
            expiringSoon={props.expiringSoon ?? false}
            repeats={props.repeats ?? false}
          />
        </div>
      </div>

      {hasRewards && (
        <div className="rewards">
          <div>{COPY.MODAL.SECTION_LABEL_REWARDS}</div>
          <div className={classNames('reward-tokens', { gray: props.locked })}>
            {props?.rewards?.map((reward) => {
              if (
                reward.__typename === 'Point' ||
                reward.__typename === 'Sweepstake'
              ) {
                return (
                  <RewardsDisplay
                    key={'' + reward.category + reward.count}
                    containerClasses={classNames(
                      getLimitedClasses(reward),
                      'badge-container'
                    )}
                    contentClasses="badge"
                    label={reward.category ?? ''}
                    subLabel={<LimitedInformation reward={reward} />}
                    hideEarnedText
                  >
                    <div
                      className={classNames(
                        'points',
                        getLimitedClasses(reward) || 'purple'
                      )}
                    >
                      {reward.count}
                    </div>
                  </RewardsDisplay>
                );
              } else if (reward.__typename === 'Badge') {
                return (
                  <RewardsDisplay
                    key={reward.title + ''}
                    containerClasses={classNames(
                      getLimitedClasses(reward),
                      'badge-container'
                    )}
                    imageUrl={reward.thumbUrl}
                    contentClasses="badge"
                    label={'Badge'}
                    subLabel={<LimitedInformation reward={reward} />}
                    hideEarnedText
                  />
                );
              }
            })}
          </div>
        </div>
      )}
      <div className="tasks">
        <div className="progress-bar-container">
          <>
            <span className="progress-bar-label">
              {COPY.MODAL.SECTION_LABEL_TASKS}
            </span>
            <span className="progress-bar-value">{percentDone}%</span>
            <LinearProgress
              className="progress-bar"
              variant="determinate"
              value={percentDone}
              color="inherit"
              style={{ flex: '0 0.67 1' }}
            />
          </>
        </div>

        <TaskList>
          {props.locked && (
            <LockedStatus as={TaskItem} reasons={props.locked} />
          )}
          {displayTasks}
        </TaskList>
      </div>
    </div>
  );
}
